import { DateTime } from 'luxon';
// @mui
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
//
import Iconify from 'src/components/iconify';
import { MilesSource } from 'src/models';

// ----------------------------------------------------------------------

// Step-by-step export instructions per provider so users know exactly which
// report we need before they hit the dropzone.
const INSTRUCTIONS: Record<string, { title: string; steps: string[] }> = {
  samsara: {
    title: 'How to export your IFTA report from Samsara',
    steps: [
      'Sign in to the Samsara dashboard (cloud.samsara.com).',
      'In the left menu open Reports, then find the Fuel & Energy section and select "IFTA Mileage".',
      'Set the date range to {PERIOD}.',
      'Click Export and choose CSV (the jurisdiction-level report with Vehicle, Jurisdiction, and Miles).',
      'Upload the downloaded file below.',
    ],
  },
  motive: {
    title: 'How to export your IFTA report from Motive',
    steps: [
      'Sign in to the Motive fleet dashboard (web.gomotive.com).',
      'Open Reports and select the IFTA section, then choose the "IFTA Summary" report.',
      'Set the report period to {PERIOD} and make sure all vehicles are included.',
      'Export the report as CSV.',
      'Upload the downloaded file below.',
    ],
  },
  counting_trucks: {
    title: 'Import straight from Counting Trucks',
    steps: [
      'If your account is linked to your Counting Trucks fleet, use the "Import miles" button below. We pull per-state miles for the quarter directly from your GPS data.',
      'Otherwise, sign in to app.countingtrucks.com, open ELD, then IFTA, set the period to {PERIOD}, and download the CSV report.',
      'Upload the downloaded file below.',
    ],
  },
  // ai_eld: {
  //   title: 'How to export your IFTA report from AI ELD',
  //   steps: [
  //     'Sign in to your AI ELD portal.',
  //     'Open Reports and select "IFTA Mileage".',
  //     'Set the report period to {PERIOD} and download it as CSV or XLSX.',
  //     'Upload the downloaded file below.',
  //   ],
  // },
  // sparkle: {
  //   title: 'How to export your IFTA report from Sparkle',
  //   steps: [
  //     'Sign in to your Sparkle ELD dashboard.',
  //     'Open Reports and select the IFTA mileage report.',
  //     'Set the report period to {PERIOD} and download it as CSV or XLSX.',
  //     'Upload the downloaded file below.',
  //   ],
  // },
  other: {
    title: 'What we need from your provider',
    steps: [
      'Export a mileage-by-state report for {PERIOD} from your ELD or GPS provider, with one row per truck per state.',
      'Any column layout works: our AI reads the header and figures out which columns hold the truck, state, and miles.',
      'CSV and XLSX files are supported, up to 20 MB.',
      'Upload the downloaded file below and confirm the detected columns.',
    ],
  },
};

// "2026 Q2" -> "Apr 1 – Jun 30, 2026 (Q2 2026)"
function periodLabel(quarter: string) {
  const match = quarter.match(/^(\d{4}) Q([1-4])$/);
  if (!match) {
    return 'your filing quarter';
  }
  const year = Number(match[1]);
  const start = DateTime.local(year, (Number(match[2]) - 1) * 3 + 1, 1);
  const end = start.plus({ months: 3 }).minus({ days: 1 });
  return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')} (Q${match[2]} ${year})`;
}

const FUEL_INSTRUCTIONS: Record<string, { title: string; steps: string[] }> = {
  efs: {
    title: 'How to export your transactions from EFS',
    steps: [
      'Sign in to EFS eManager.',
      'Open Reports, then choose the transaction detail report.',
      'Set the date range to {PERIOD} and include all cards.',
      'Export as CSV and upload it here.',
    ],
  },
  tch: {
    title: 'How to export your transactions from TCH',
    steps: [
      'Sign in to your TCH online account.',
      'Open Reports and choose the transaction listing.',
      'Set the date range to {PERIOD}.',
      'Export as CSV and upload it here.',
    ],
  },
  comdata: {
    title: 'How to export your transactions from Comdata',
    steps: [
      'Sign in to iConnectData.',
      'Open Reporting and pick the fuel transaction report.',
      'Set the date range to {PERIOD}.',
      'Export as CSV and upload it here.',
    ],
  },
  fleet_one: {
    title: 'How to export your transactions from Fleet One',
    steps: [
      'Sign in to your Fleet One account.',
      'Open Reports and pick the transaction detail report.',
      'Set the date range to {PERIOD}.',
      'Export as CSV and upload it here.',
    ],
  },
  other: {
    title: 'What we need from your fuel card provider',
    steps: [
      'Export a fuel purchase report for {PERIOD}, with one row per transaction or per truck per state.',
      'Any column layout works: our AI reads the header and figures out which columns hold the truck, state, gallons, and amount.',
      'CSV and XLSX files are supported, up to 20 MB.',
      'Upload the downloaded file here.',
    ],
  },
};

type Props = {
  source: MilesSource | string;
  quarter: string;
  kind?: 'miles' | 'fuel';
};

export default function MilesInstructions({ source, quarter, kind = 'miles' }: Props) {
  const map = kind === 'fuel' ? FUEL_INSTRUCTIONS : INSTRUCTIONS;
  const instructions = map[source || 'other'] || map.other;
  const period = periodLabel(quarter);

  return (
    <Card variant="outlined" sx={{ p: 2.5, bgcolor: '#FAFCFA' }} data-testid="miles-instructions">
      <Stack direction="row" spacing={1.5}>
        <Iconify
          icon="solar:info-circle-bold"
          width={22}
          sx={{ color: 'primary.dark', flexShrink: 0, mt: '2px' }}
        />
        <Stack spacing={1}>
          <Typography variant="subtitle2">{instructions.title}</Typography>
          <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {instructions.steps.map((step, index) => (
              <Box component="li" key={index}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {step.replace('{PERIOD}', period)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
